import { FC, Fragment, useCallback, useEffect, useMemo, useState } from "react"
import { ColumnFiltersState } from "@tanstack/react-table"
import { Popover, Transition } from "@headlessui/react"
import { Plus, SlidersHorizontal, Settings2 } from "lucide-react"
import {
  useAppDispatch,
  useAppSelector,
  useInMobileView,
  useToast
} from "../../../hooks"
import {
  LoanOfficer,
  BBColumnDef,
  ViewType,
  LoSearchParams
} from "../../../../types"
import { If, PrimaryBtn, Table } from "../../../ui"
import { masquerade, selectUser } from "../../../../redux/slices/auth"
import { setVisiblePanel } from "../../../../redux/slices/app"
import { BrandCell, EmailCell, MenuCell, UserCell } from "./cells"
import LOCard from "./card"
import { ToggleView } from "../../table/toolbar"
import EditLoProfile from "../../account/edit-lo-profile"
import BBDialog from "../../dialog"
import CardSkeleton from "../../skeletons/normal-card"
import ListSkeleton from "../../skeletons/normal-list"
import {
  useDeleteLoanOfficerMutation,
  useFetchLoanOfficersQuery
} from "../../../../redux/queries"

export interface IDeleteLO {
  state: boolean
  loid: string
}

const LOFilters: FC<{
  los: LoanOfficer[]
  columnFilters: ColumnFiltersState
  setColumnFilters: (
    filters:
      | ColumnFiltersState
      | ((prev: ColumnFiltersState) => ColumnFiltersState)
  ) => void
  shrinkFilter?: boolean
}> = ({ los, columnFilters, setColumnFilters, shrinkFilter = false }) => {
  const uniqueBrands = useMemo(() => {
    const brands = new Set<string>()
    los.forEach(lo => {
      if (lo.bank) brands.add(lo.bank)
    })
    return Array.from(brands).sort()
  }, [los])

  const selectedBrand = columnFilters.find(f => f.id === "bank")?.value as
    | string
    | undefined

  const isBrandAdminChecked = columnFilters.some(
    f => f.id === "brandAdmin" && f.value === "yes"
  )

  const toggleBrandFilter = (brand: string) => {
    if (selectedBrand === brand) {
      setColumnFilters(prev => prev.filter(f => f.id !== "bank"))
    } else {
      setColumnFilters(prev => [
        ...prev.filter(f => f.id !== "bank"),
        { id: "bank", value: brand }
      ])
    }
  }

  const toggleBrandAdminFilter = () => {
    if (isBrandAdminChecked) {
      setColumnFilters(prev => prev.filter(f => f.id !== "brandAdmin"))
    } else {
      setColumnFilters(prev => [...prev, { id: "brandAdmin", value: "yes" }])
    }
  }

  return (
    <div className="flex items-center">
      <Popover as="div" className="relative z-10 inline-block text-left">
        <Popover.Button className="group inline-flex items-center justify-center appearance-none !px-4 !py-2 border !border-slate-300 !rounded-lg placeholder-gray-400 focus:outline-none hover:bg-primary-50 hover:!border-primary-300 hover:text-primary-600 sm:text-sm">
          <SlidersHorizontal
            size={16}
            className={`flex-shrink-0 mr-1 text-base-dark group-hover:text-primary-600 ${
              shrinkFilter ? "hidden sm:block" : ""
            }`}
            aria-hidden="true"
          />
          <Settings2
            size={16}
            className={`flex-shrink-0 text-base-dark group-hover:text-primary-600 my-1 ${
              shrinkFilter ? "block sm:hidden" : "hidden"
            }`}
            aria-hidden="true"
          />
          <span className={shrinkFilter ? "sr-only sm:not-sr-only" : ""}>
            Filters
          </span>
        </Popover.Button>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Popover.Panel className="origin-top-left absolute left-0 mt-2 bg-white rounded-md shadow-2xl p-4 ring-1 ring-black ring-opacity-5 focus:outline-none z-10 min-w-[180px]">
            <h3 className="sr-only">Filters</h3>
            <div className="space-y-4">
              {uniqueBrands.length > 1 && (
                <>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Brand
                  </div>
                  {uniqueBrands.map((brand, idx) => (
                    <label
                      key={brand}
                      htmlFor={`filter-bank-${idx}`}
                      className="flex items-center cursor-pointer"
                    >
                      <input
                        id={`filter-bank-${idx}`}
                        name="bank"
                        checked={selectedBrand === brand}
                        onChange={() => toggleBrandFilter(brand)}
                        type="checkbox"
                        className="h-4 w-4 border-gray-300 rounded text-indigo-500 focus:ring-indigo-400"
                      />
                      <span className="ml-3 pr-6 text-sm font-medium text-gray-900 whitespace-nowrap">
                        {brand}
                      </span>
                    </label>
                  ))}
                  <hr className="border-gray-200" />
                </>
              )}
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Role
              </div>
              <label
                htmlFor="filter-brandAdmin"
                className="flex items-center cursor-pointer"
              >
                <input
                  id="filter-brandAdmin"
                  name="brandAdmin"
                  checked={isBrandAdminChecked}
                  onChange={toggleBrandAdminFilter}
                  type="checkbox"
                  className="h-4 w-4 border-gray-300 rounded text-indigo-500 focus:ring-indigo-400"
                />
                <span className="ml-3 pr-6 text-sm font-medium text-gray-900 whitespace-nowrap">
                  Brand Admins
                </span>
              </label>
            </div>
          </Popover.Panel>
        </Transition>
      </Popover>
    </div>
  )
}

interface Props {
  searchParams: LoSearchParams
  defaultViewType?: ViewType
  hasViewTypeToggle?: boolean
  hasNewButton?: boolean
  hasMasqueradeIcon?: boolean
  onClickLO?: (loID: string) => void
}

const LOsTable: FC<Props> = ({
  searchParams,
  defaultViewType = "table",
  hasViewTypeToggle = true,
  hasNewButton = false,
  hasMasqueradeIcon = false,
  onClickLO
}) => {
  const dispatch = useAppDispatch()

  const { pushSuccessToast, pushErrorToast } = useToast()

  const [deleteLoanOfficer, { isSuccess, isError }] =
    useDeleteLoanOfficerMutation()

  const user = useAppSelector(selectUser)?.user
  const myUserId = user?.id

  const [viewType, setViewType] = useState<ViewType>(defaultViewType)
  const [showDeletePopover, setShowDeletePopover] = useState(false)
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])

  const pageSize = viewType === "table" ? 10 : 12

  const [selectedLo, setSelectedLo] = useState("")
  const [deleteLO, setDeleteLO] = useState("")
  const [isFetching, setIsFetching] = useState(true)
  const inMobileView = useInMobileView()

  const { data, isLoading } = useFetchLoanOfficersQuery({
    ...searchParams
  })

  useEffect(() => {
    setIsFetching(isLoading)
  }, [isLoading])

  const los: LoanOfficer[] = useMemo(() => {
    return data?.los ?? []
  }, [data])

  const selectedLoDetails = useMemo(
    () => los?.find(item => item.uid === selectedLo),
    [los, selectedLo]
  )

  const closeEditLOPanel = () => {
    setSelectedLo("")
  }

  const onMasquerade = useCallback(
    (loid: string) => {
      if (myUserId !== loid) {
        dispatch(masquerade(loid))
      }
    },
    [myUserId, dispatch]
  )

  const onEdit = useCallback(
    (loanOfficer: LoanOfficer) => {
      setSelectedLo(loanOfficer.uid)
    },
    [setSelectedLo]
  )

  const handleConfirmDialog = useCallback(() => {
    setShowDeletePopover(false)
    deleteLoanOfficer(deleteLO)
  }, [deleteLoanOfficer, deleteLO])

  const handleCancelDialog = useCallback(() => {
    setDeleteLO("")
    setShowDeletePopover(false)
  }, [setDeleteLO, setShowDeletePopover])

  const handleCloseDialog = useCallback(() => {
    setShowDeletePopover(false)
  }, [setShowDeletePopover])

  // delete LO
  const onDelete = useCallback(
    (loid: string) => {
      setDeleteLO(loid)
      setShowDeletePopover(true)
    },
    [setDeleteLO, setShowDeletePopover]
  )

  const openCreatePanel = useCallback(() => {
    dispatch(setVisiblePanel("loan-officer/new"))
  }, [dispatch])

  useEffect(() => {
    if (isSuccess) {
      pushSuccessToast("Loan Officer deleted successfully")
    } else if (isError) {
      pushErrorToast("Failed to delete Loan Officer")
    }
  }, [pushSuccessToast, pushErrorToast, isSuccess, isError])

  const columns = useMemo<BBColumnDef<LoanOfficer>[]>(() => {
    const isMasqueradeEnabled = (rowUid: string) =>
      hasMasqueradeIcon && user?.is_masq !== "1" && myUserId !== rowUid

    return [
      {
        id: "name",
        header: "Name",
        accessorFn: row => row.name,
        footer: props => props.column.id,
        cell: props => (
          <UserCell
            onUserCellClick={onClickLO}
            info={props.row.original}
            masqueradeEnabled={isMasqueradeEnabled(props.row.original.uid)}
            onMasquerade={onMasquerade}
          />
        ),
        colSpan: 2
      },
      {
        id: "bank",
        header: "Brand",
        accessorFn: row => row.bank,
        footer: props => props.column.id,
        cell: props => (
          <BrandCell onBrandCellClick={onClickLO} info={props.row.original} />
        ),
        meta: { headerClassName: "hidden md:table-cell" }
      },
      {
        id: "email",
        header: "Email",
        accessorFn: row => row.email,
        footer: props => props.column.id,
        cell: props => (
          <EmailCell onEmailCellClick={onClickLO} info={props.row.original} />
        ),
        meta: { headerClassName: "hidden md:table-cell" }
      },
      {
        id: "edit",
        header: "",
        footer: props => props.column.id,
        cell: props => (
          <MenuCell
            info={props.row.original}
            masqueradeEnabled={isMasqueradeEnabled(props.row.original.uid)}
            onMasquerade={onMasquerade}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        )
      },
      {
        id: "brandAdmin",
        accessorFn: row => (row.roles?.includes("bank admin") ? "yes" : "no"),
        header: () => null,
        cell: () => null,
        meta: { headerClassName: "hidden" }
      }
    ]
  }, [
    onClickLO,
    onMasquerade,
    onEdit,
    onDelete,
    hasMasqueradeIcon,
    user?.is_masq,
    myUserId
  ])

  const hasExtraHeaderElement =
    hasNewButton || (hasViewTypeToggle && !inMobileView)

  return (
    <>
      <If
        condition={isFetching}
        then={
          <If
            condition={viewType === "table"}
            then={<ListSkeleton />}
            else={<CardSkeleton />}
          />
        }
        else={
          <>
            <Table
              data={los ?? []}
              columns={columns}
              viewType={viewType}
              pageSize={pageSize}
              columnFilters={columnFilters}
              setColumnFilters={setColumnFilters}
              autoResetPageIndex={false}
              searchPlaceholder="Search LO's by name, email or brand…"
              renderCard={row => (
                <LOCard
                  onClickLO={onClickLO}
                  onEditLO={onEdit}
                  row={row}
                  onDeleteLO={onDelete}
                />
              )}
              customFilterComponent={
                <LOFilters
                  los={los}
                  columnFilters={columnFilters}
                  setColumnFilters={setColumnFilters}
                  shrinkFilter
                />
              }
              extraHeaderElement={
                hasExtraHeaderElement ? (
                  <>
                    {hasViewTypeToggle ? (
                      <ToggleView
                        className="ml-auto"
                        viewType={viewType}
                        setViewType={setViewType}
                      />
                    ) : null}
                    {hasNewButton ? (
                      <PrimaryBtn onClick={openCreatePanel}>
                        <Plus size={16} />
                        &nbsp;New LO
                      </PrimaryBtn>
                    ) : null}
                  </>
                ) : null
              }
              shrinkFilterSort
            />
          </>
        }
      />
      <EditLoProfile
        data={selectedLoDetails}
        open={!!selectedLo}
        isInSidePanel
        onClose={closeEditLOPanel}
      />
      <BBDialog
        open={showDeletePopover}
        title="Delete Loan Officer Profile"
        body="By doing this all the information stored in the system about this loan officer will be deleted. Are you sure ?"
        okLabel="Yes, Delete"
        onOk={handleConfirmDialog}
        onCancel={handleCancelDialog}
        onClose={handleCloseDialog}
        cancelLabel="Cancel"
      />
    </>
  )
}

export default LOsTable
